import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCalculation } from "../../context/CalculationContext";
import { alpha } from "../../theme/tokens";
import { useDesignTokens } from "../../context/ThemeContext";
import {
    Card,
    CardHeader,
    CardContent,
    Button,
    Box,
    IconButton,
    Typography,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    Modal,
    Fade,
    Backdrop,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
} from "@mui/material";
import { Close, Visibility, FileDownload, PictureAsPdf } from "@mui/icons-material";
import TorisphericalVesselCalculation from "./TorisphericalVesselCalculation";
import PipeDimensionCalculation from "./PipeDimensionCalculation";
import CylindricalShellCalculation from "./CylindricalShellCalculation";
import DishedHeadCalculation from "./DishedHeadCalculation";
import BudgetDimensionsTable from "./BudgetDimensionsTable";
import BudgetCostSummary from "./BudgetCostSummary";
import DataInputStep, { PreviewTable } from "./DataInputStep";
import {
    editorFields,
    flattenFields,
    isValidEmail,
    getInitialFormData,
} from "./budgetFormConfig";
import {
    generateBudgetDimensionsPdf,
    calculateTorisphericalVessel,
    calculateCylindricalShell,
    calculateDishedHead,
    getBudgetDimensions,
} from "../../services/budgets/BudgetService";
import { buildDimensionsPdfPayload } from "../../services/budgets/budgetPdfPayload";
import { getUsername } from "../../services/users/AuthService";

const getNewBudgetDraftKey = () => `budgetDraft:${getUsername() || "anonymous"}:new`;

const readNewBudgetDraft = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(getNewBudgetDraftKey()));
        return saved?.formData && typeof saved.formData === "object" ? saved : null;
    } catch {
        return null;
    }
};

const useFormValidation = (fields, formData, errors) => {
    const flattenedFields = useMemo(() => flattenFields(fields), [fields]);

    const isValid = useMemo(() => {
        return flattenedFields.every((field) => {
            if (!field.required) return true;
            const value = formData[field.name];
            const hasError = errors[field.name];
            if (hasError) return false;
            if (field.type === "file") return value !== null;
            return value !== "" && value !== undefined && value !== null;
        });
    }, [flattenedFields, formData, errors]);

    return isValid;
};

const useFieldErrors = () => {
    const [errors, setErrors] = useState({});
    const { t } = useTranslation();

    const validateField = useCallback((name, value, type) => {
        if (type === "email") {
            if (value && !isValidEmail(value)) {
                setErrors((prev) => ({ ...prev, [name]: t("budgetForm.dataInput.emailInvalid") }));
            } else {
                setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
    }, [t]);

    return { errors, validateField };
};

const OutputStep = ({ data, loading, budgetCost, error, budgetId, results }) => {
    const { t, i18n } = useTranslation();
    const [generatingPdf, setGeneratingPdf] = useState(false);

    const handleGeneratePdf = useCallback(async () => {
        if (!budgetId || generatingPdf) return;
        setGeneratingPdf(true);
        try {
            // The PDF renders every calculation section, so make sure all outputs
            // are computed even if the user skipped straight to this step.
            const [vessel, cylindrical, dishedHead, dimensions] = await Promise.all([
                results?.vessel ?? calculateTorisphericalVessel(budgetId),
                results?.cylindrical ?? calculateCylindricalShell(budgetId),
                results?.dishedHead ?? calculateDishedHead(budgetId),
                results?.dimensions ?? getBudgetDimensions(budgetId),
            ]);
            const payload = buildDimensionsPdfPayload({ vessel, cylindrical, dishedHead, dimensions }, t);

            const blob = await generateBudgetDimensionsPdf(budgetId, i18n.language, payload);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `budget_${budgetId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error generating budget PDF:", err.response?.data || err.message);
        } finally {
            setGeneratingPdf(false);
        }
    }, [budgetId, generatingPdf, i18n.language, results, t]);

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 1, color: "primary.main", textAlign: "center" }}>
                {t("budgetForm.outputStep.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
                {t("budgetForm.outputStep.subtitle")}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", pr: { xs: 1, md: 3 }, mb: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={generatingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                    disabled={!budgetId || generatingPdf}
                    onClick={handleGeneratePdf}
                >
                    {t("budgetForm.outputStep.generatePdf")}
                </Button>
            </Box>

            <BudgetDimensionsTable data={data} loading={loading} />

            <BudgetCostSummary data={budgetCost} loading={loading} error={error} />
        </Box>
    );
};

const BudgetEditorView = ({ title,
    onSubmit,
    buttonTitle,
    initialData,
    budgetsForImport = [],
    activeStep = 0,
    onStepChange,
    oncloseBudgetEditor }) => {

    const { t } = useTranslation();
    const { budgetId, setBudgetId, results, loading, error: calculationError, runStepCalculation } = useCalculation();
    const tokens = useDesignTokens();

    const fields = editorFields;

    const savedDraft = useMemo(() => initialData ? null : readNewBudgetDraft(), [initialData]);
    const [activeTab, setActiveTab] = useState(() => savedDraft?.activeTab || 0);
    const [formData, setFormData] = useState(() => ({
        ...getInitialFormData(fields, initialData),
        ...(savedDraft?.formData || {}),
    }));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { errors, validateField } = useFieldErrors();

    const sections = fields.filter((f) => f.type === "section");
    const isFormValid = useFormValidation(fields, formData, errors);

    const stepKeys = [
        "budgetForm.steps.dataInput",
        "budgetForm.steps.deaeratorCalculations",
        "budgetForm.steps.heatingPipeDiameter",
        "budgetForm.steps.cylindricalShell",
        "budgetForm.steps.dishedHead",
        "budgetForm.steps.budget",
    ];
    const steps = stepKeys.map((key) => t(key));

    const handleChange = useCallback(
        (name, value) => {

            setFormData((prev) => ({ ...prev, [name]: value }));

            const flatFields = flattenFields(fields);
            const field = flatFields.find((f) => f.name === name);

            if (field?.type === "email") {
                validateField(name, value, "email");
            }
        },
        [fields, validateField]
    );

    const handleTabChange = (e, newValue) => {
        setActiveTab(newValue);
    };


    const handleSubmitData = async () => {
        if (!isFormValid) return;

        setIsSubmitting(true);

        try {
            const result = await onSubmit(formData);
            formData.language = formData.language === "PT" ? "Português" : "Inglês";

            if (result?.id) {
                if (isCreateMode) localStorage.removeItem(getNewBudgetDraftKey());
                setBudgetId(result.id);
                if (onStepChange) onStepChange(1);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        if (onStepChange) {
            onStepChange(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (onStepChange) {
            onStepChange(activeStep - 1);
        }
    };

    const closeForm = () => {
        if (oncloseBudgetEditor) {
            oncloseBudgetEditor();
        }
    };

    const handleClearAll = () => {
        setFormData(getInitialFormData(fields, null));
        if (isCreateMode) localStorage.removeItem(getNewBudgetDraftKey());
    };

    const handleFinalSubmit = () => {
        closeForm();
    };

    const [previewOpen, setPreviewOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importSearch, setImportSearch] = useState("");

    const isCreateMode = !initialData;

    const filteredImportBudgets = budgetsForImport.filter((b) =>
        (b.projectInfo?.propose || "").toLowerCase().includes(importSearch.toLowerCase()) ||
        (b.projectInfo?.projectCode || "").toLowerCase().includes(importSearch.toLowerCase()) ||
        (b.projectInfo?.location || "").toLowerCase().includes(importSearch.toLowerCase())
    );

    useEffect(() => {
        runStepCalculation(activeStep);
    }, [activeStep, budgetId, runStepCalculation]);

    useEffect(() => {
        if (!isCreateMode) return;

        const fileFields = new Set(
            flattenFields(fields)
                .filter((field) => field.type === "file")
                .map((field) => field.name)
        );
        const serializableFormData = Object.fromEntries(
            Object.entries(formData).filter(([name]) => !fileFields.has(name))
        );

        try {
            localStorage.setItem(getNewBudgetDraftKey(), JSON.stringify({
                formData: serializableFormData,
                activeTab,
                savedAt: new Date().toISOString(),
            }));
        } catch {
            // A full/disabled storage area must not block editing the budget.
        }
    }, [activeTab, fields, formData, isCreateMode]);

    return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1, px: 2 }}>
            <Card sx={{ width: "100%", p: 1, borderRadius: 3, boxShadow: 4 }}>
                <CardHeader
                    title={title || t("budgetForm.editor.formTitleFallback")}
                    sx={{ textAlign: "center", position: "relative", fontWeight: "bold" }}
                    action={
                        <IconButton
                            aria-label={t("budgetForm.editor.close")}
                            onClick={closeForm}
                            sx={{
                                backgroundColor: "error.main",
                                color: "#fff",
                                position: "absolute",
                                right: 10,
                                top: 10,
                                "&:hover": {
                                    backgroundColor: "error.dark"
                                }
                            }}
                        >
                            <Close />
                        </IconButton>
                    }
                />

                <Box sx={{ px: { xs: 1, md: 4 }, pb: 3, mt: 4 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label, i) => (
                            <Step key={label}>
                                <StepLabel
                                    sx={{
                                        "& .MuiStepLabel-label": {
                                            fontSize: { xs: "0.65rem", md: "0.75rem", lg: "0.875rem" },
                                            display: { xs: i === activeStep ? "block" : "none", md: "block" },
                                        },
                                        "& .MuiStepLabel-label.Mui-active": {
                                            color: "primary.main",
                                            fontWeight: "bold",
                                        },
                                        "& .MuiStepLabel-label.Mui-completed": {
                                            color: "primary.main",
                                        },
                                        "& .MuiStepIcon-root.Mui-active": {
                                            color: "primary.main",
                                        },
                                        "& .MuiStepIcon-root.Mui-completed": {
                                            color: "primary.main",
                                        },
                                    }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {activeStep !== 0 && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", px: 4, pb: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => setPreviewOpen(true)}
                            sx={{
                                borderColor: "primary.main",
                                color: "primary.main",
                                textTransform: "none",
                                "&:hover": {
                                    borderColor: "primary.dark",
                                    backgroundColor: alpha.primary04,
                                },
                            }}
                        >
                            {t("budgetForm.editor.viewData")}
                        </Button>
                    </Box>
                )}

                <CardContent>
                    <Box sx={{ display: activeStep === 0 ? "block" : "none" }}>
                        <>
                            {isCreateMode && (
                                <Box sx={{ display: "flex", gap: 1, mb: 2, justifyContent: "flex-end" }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownload />}
                                        onClick={() => setImportModalOpen(true)}
                                        sx={{
                                            borderColor: "primary.main",
                                            color: "primary.main",
                                            textTransform: "none",
                                            "&:hover": {
                                                borderColor: "primary.dark",
                                                backgroundColor: alpha.primary04,
                                            },
                                        }}
                                    >
                                        {t("budgetForm.editor.importData")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleClearAll}
                                        sx={{
                                            borderColor: "error.dark",
                                            color: "error.dark",
                                            textTransform: "none",
                                            "&:hover": {
                                                borderColor: tokens.errorDarker,
                                                backgroundColor: alpha.error04,
                                            },
                                        }}
                                    >
                                        {t("budgetForm.editor.clearAll")}
                                    </Button>
                                </Box>
                            )}
                            <DataInputStep
                                sections={sections}
                                activeTab={activeTab}
                                handleTabChange={handleTabChange}
                                formData={formData}
                                handleChange={handleChange}
                                errors={errors}
                            />
                        </>
                    </Box>


                    {activeStep === 1 && <TorisphericalVesselCalculation
                        data={results.vessel}
                        loading={loading}
                        error={calculationError}
                    />}

                    {activeStep === 2 && <PipeDimensionCalculation data={results.vessel} loading={loading} error={calculationError} />}

                    {activeStep === 3 && <CylindricalShellCalculation data={results.cylindrical} loading={loading} error={calculationError} />}

                    {activeStep === 4 && <DishedHeadCalculation data={results.dishedHead} loading={loading} error={calculationError} />}

                    {activeStep === 5 && (
                        <OutputStep
                            results={results}
                            data={results.dimensions}
                            budgetCost={results.budgetCost}
                            loading={loading}
                            error={calculationError}
                            budgetId={budgetId}
                        />
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, px: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleBack}
                            disabled={activeStep === 0 || isSubmitting}
                            sx={{
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                    borderColor: "primary.dark",
                                    backgroundColor: alpha.primary04,
                                },
                            }}
                        >
                            {t("budgetForm.editor.previous")}
                        </Button>

                        {activeStep === 0 ? (
                            <Button
                                variant="contained"
                                onClick={handleSubmitData}
                                disabled={!isFormValid || isSubmitting}
                                sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    minWidth: 200,
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                        {t("budgetForm.editor.submitting")}
                                    </>
                                ) : (
                                    t("budgetForm.editor.saveContinue")
                                )}
                            </Button>
                        ) : activeStep === steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleFinalSubmit}
                                sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    minWidth: 200,
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                }}
                            >
                                {buttonTitle || 'Finalizar'}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={!budgetId}
                                sx={{
                                    backgroundColor: "primary.main",
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: "primary.dark",
                                    },
                                }}
                            >
                                {t("budgetForm.editor.next")}
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>

            <Modal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 300 } }}
            >
                <Fade in={previewOpen}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: { xs: "95%", sm: 600, md: 700 },
                            maxHeight: "90vh",
                            overflow: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            boxShadow: 24,
                            p: 3,
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                {t("budgetForm.editor.previewModal.title")}
                            </Typography>
                            <IconButton
                                onClick={() => setPreviewOpen(false)}
                                sx={{
                                    backgroundColor: "error.main",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "error.dark"
                                    }
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Box>
                        <PreviewTable sections={sections} formData={formData} />
                    </Box>
                </Fade>
            </Modal>

            <Dialog
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: "primary.main" }}>
                    {t("budgetForm.editor.importModal.title")}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t("budgetForm.editor.importModal.searchPlaceholder")}
                        value={importSearch}
                        onChange={(e) => setImportSearch(e.target.value)}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: alpha.primary30 }}>
                                    <TableCell sx={{ fontWeight: 700 }}>{t("budgetForm.editor.importModal.colProposal")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t("budgetForm.editor.importModal.colCode")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t("budgetForm.editor.importModal.colLocation")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t("budgetForm.editor.importModal.colDate")}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{t("budgetForm.editor.importModal.colEnvironment")}</TableCell>
                                    <TableCell sx={{ width: 90 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredImportBudgets.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                            {t("budgetForm.editor.importModal.empty")}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredImportBudgets.map((b) => (
                                        <TableRow
                                            key={b.id}
                                            hover
                                            sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                                            onClick={() => {
                                                setFormData(getInitialFormData(editorFields, b));
                                                setImportModalOpen(false);
                                                setImportSearch("");
                                            }}
                                        >
                                            <TableCell>{b.projectInfo?.propose || "—"}</TableCell>
                                            <TableCell>{b.projectInfo?.projectCode || "—"}</TableCell>
                                            <TableCell>{b.projectInfo?.location || "—"}</TableCell>
                                            <TableCell>{b.createdAt?.split("T")[0] || "—"}</TableCell>
                                            <TableCell>{b.projectInfo?.enviroment || "—"}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: "primary.main" }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFormData(getInitialFormData(editorFields, b));
                                                        setImportModalOpen(false);
                                                        setImportSearch("");
                                                    }}
                                                >
                                                    <FileDownload fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setImportModalOpen(false)}
                        sx={{ color: "#757575", textTransform: "none" }}
                    >
                        {t("budgetForm.editor.importModal.cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default BudgetEditorView;
